import { formatInvoiceNumber, useIntegrityReport } from "@/api/invoices";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/** The server's numbering and stored-PDF check, with each financial-year series. */
export function IntegrityPanel() {
  const report = useIntegrityReport();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoice numbering check</CardTitle>
        <CardDescription>
          Confirms every financial-year series runs without gaps and every stored PDF is
          unchanged since it was issued.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {report.isPending ? (
          <p className="text-muted-foreground">Checking…</p>
        ) : report.isError ? (
          <RequestError error={report.error} />
        ) : (
          <>
            {report.data.ok ? (
              <Alert variant="success" title="Numbers and PDFs check out">
                {report.data.invoices_checked} invoices, {report.data.receipts_checked} receipts
                and {report.data.documents_checked + report.data.receipt_documents_checked} stored
                PDFs checked.
              </Alert>
            ) : (
              <Alert variant="error" title="Problems found - tell the other directors">
                <ul className="list-disc pl-5">
                  {report.data.problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              </Alert>
            )}
            {report.data.series.length > 0 && (
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 font-medium">Year</th>
                      <th className="px-4 py-2 font-medium">First number</th>
                      <th className="px-4 py-2 font-medium">Next number</th>
                      <th className="px-4 py-2 text-right font-medium">Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.data.series.map((series) => (
                      <tr key={`${series.doc_type}-${series.fy_code}`} className="border-b last:border-0">
                        <td className="px-4 py-2">FY {series.fy_code}</td>
                        <td className="whitespace-nowrap px-4 py-2">
                          {formatInvoiceNumber(series.prefix, series.fy_code, series.first_serial)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 font-medium">
                          {formatInvoiceNumber(series.prefix, series.fy_code, series.next_serial)}
                        </td>
                        <td className="px-4 py-2 text-right">{series.numbers_used}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        <div>
          <Button
            size="sm"
            variant="outline"
            disabled={report.isFetching}
            onClick={() => void report.refetch()}
          >
            {report.isFetching ? "Checking…" : "Run the check again"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
